<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
-->

<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.cfg_finance') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <oxd-form :loading="saving" @submit-valid="onAdd">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="newMember"
              :params="{includeEmployees: 'onlyCurrent'}"
              :rules="rules.member"
            />
          </oxd-grid-item>
          <oxd-grid-item class="expensa-add">
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('general.add')"
              :disabled="saving"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
    </oxd-form>
    <oxd-divider />
    <oxd-text
      v-if="state === 'loading'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.loading') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.finance_empty') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="rows"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
  </div>
</template>

<script>
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import {required, validSelection} from '@/core/util/validation/rules';
import {expensaApi, errorMessage} from '../../util/api';
import {personName} from '../../util/format';

// Elenco finance: solo l'Admin lo vede e lo modifica (lo impone il server).
export default {
  name: 'ExpensaFinanceSection',
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  data() {
    return {
      state: 'loading',
      members: [],
      newMember: null,
      saving: false,
      rules: {member: [required, validSelection]},
    };
  },
  computed: {
    rows() {
      return this.members.map((m) => ({
        id: m.empNumber,
        name: personName(m),
        active: m.active ? this.$t('general.yes') : this.$t('general.no'),
        isActive: m.active,
      }));
    },
    headers() {
      return [
        {name: 'name', title: this.$t('general.name'), style: {flex: 3}},
        {name: 'active', title: this.$t('general.active'), style: {flex: 1}},
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 2},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            toggle: {
              onClick: this.onToggle,
              component: 'oxd-button',
              props: {
                label: this.$t('expensa.toggle_active'),
                displayType: 'text',
              },
            },
          },
        },
      ];
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const {data} = await expensaApi('/finance').getAll();
        this.members = data.data;
        this.state = this.members.length === 0 ? 'empty' : 'list';
      } catch (e) {
        this.state = 'error';
      }
    },
    async run(call) {
      this.saving = true;
      try {
        await call();
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_done'),
        });
        await this.load();
        return true;
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
        return false;
      } finally {
        this.saving = false;
      }
    },
    async onAdd() {
      if (!this.newMember) return;
      const done = await this.run(() =>
        expensaApi('/finance').create({empNumber: this.newMember.id}),
      );
      if (done) this.newMember = null;
    },
    onToggle(item) {
      return this.run(() =>
        expensaApi('/finance').update(item.id, {active: !item.isActive}),
      );
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
.expensa-add {
  align-self: end;
}
</style>
